import { useAuth, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type MeasurementTypeFilter = 'body_weight' | 'blood_pressure' | 'heart_rate'

export interface MeasurementItem {
  id: string
  measurement_type: MeasurementTypeFilter | 'unknown'
  measured_at: string
  notes?: string
  source?: string
  mass_kg?: string
  systolic_mmhg?: number
  diastolic_mmhg?: number
  pulse_bpm?: number | null
  bpm?: number
  created_at: string
  updated_at: string
}

export interface MeasurementFilters {
  measurement_type?: MeasurementTypeFilter
  start_at?: string
  end_at?: string
  limit?: number
}

export interface CreateMeasurementData {
  measurement_type: MeasurementTypeFilter
  measured_at?: string
  notes?: string
  weight?: string
  weight_unit?: 'kg' | 'lb'
  systolic_mmhg?: number
  diastolic_mmhg?: number
  pulse_bpm?: number | null
  bpm?: number
}

export interface UpdateMeasurementData {
  measurement_id: string
  measured_at?: string
  notes?: string
  weight?: string
  weight_unit?: 'kg' | 'lb'
  systolic_mmhg?: number
  diastolic_mmhg?: number
  pulse_bpm?: number | null
  bpm?: number
}

export function useMeasurements(filters: MeasurementFilters = {}) {
  const { token } = useAuth()
  const queryKey = ['measurements', filters]

  const q = useQuery({
    queryKey,
    queryFn: async (): Promise<MeasurementItem[]> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams()
      if (filters.measurement_type) params.append('measurement_type', filters.measurement_type)
      if (filters.start_at) params.append('start_at', filters.start_at)
      if (filters.end_at) params.append('end_at', filters.end_at)
      if (filters.limit) params.append('limit', String(filters.limit))
      const qs = params.toString()
      const url = `/api/measurements/${qs ? `?${qs}` : ''}`
      const response = await authorizedFetch(url)
      if (!response.ok) throw new Error('Failed to fetch measurements')
      return response.json()
    },
    enabled: !!token,
    staleTime: 30 * 1000,
  })

  return {
    measurements: q.data ?? [],
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  }
}

export function useCreateMeasurement() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateMeasurementData): Promise<MeasurementItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch('/api/measurements/', {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error((errorData as { error?: string }).error || 'Failed to create measurement')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurements'] })
    },
  })
}

export function useUpdateMeasurement() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: UpdateMeasurementData): Promise<MeasurementItem> => {
      if (!token) throw new Error('Not authenticated')
      const { measurement_id, ...body } = data
      const response = await authorizedFetch(`/api/measurements/${measurement_id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error((errorData as { error?: string }).error || 'Failed to update measurement')
      }
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['measurements'] })
      queryClient.invalidateQueries({ queryKey: ['measurements', variables.measurement_id] })
    },
  })
}

export function useDeleteMeasurement() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (measurementId: string): Promise<void> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`/api/measurements/${measurementId}`, { method: 'DELETE' })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error((errorData as { error?: string }).error || 'Failed to delete measurement')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurements'] })
    },
  })
}
